import React, { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreVerticalIcon } from "lucide-react";
import CommonDelete from "../Delete";
import { singleProp } from "@/interface/dataInterface";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { PartnerData } from "@/interface/entityInterface";
import axiosInstance from "@/utils/axiosInstance";
import CommissionPartnerCell from "@/tours-agent/orders/CommissionPartnerCell";

type TableColOptions<T> = {
  showActions?: boolean;
  columns?: ColumnDef<T>[];
  EditComponent?: React.ComponentType<singleProp>;
  resource?: string;
  responseMessage?: any;
  id?: any;
};

export function tableCol<T>({
  showActions = false,
  columns = [],
  EditComponent,
  resource,
  responseMessage,
  id,
}: TableColOptions<T>) {
  const baseCols: ColumnDef<T>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ row }) => <div className="capitalize">{row.getValue("id")}</div>,
    },
    ...columns,
  ];

  if (showActions) {
    baseCols.push({
      id: "actions",
      cell: ({ row }: { row: { original: T } }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
              size="icon"
            >
              <MoreVerticalIcon />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-32">
            {EditComponent && (
              <>
                <EditComponent
                  prop={id ? id(row.original) : row.original.id}
                />
                <DropdownMenuSeparator />
              </>
            )}
            <CommonDelete
              id={(row.original as any).id}
              resource={resource || ""}
              responseMessage={
                typeof responseMessage === "function"
                  ? responseMessage(row.original)
                  : responseMessage || ""
              }
            />
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    });
  }

  return baseCols;
}

export function ordersTableCol<T>({
  showActions,
  columns = [],
  EditComponent,
  responseMessage,
  resource,
}: TableColOptions<T>) {
  const [partners, setPartners] = useState<PartnerData[]>([]);

  useEffect(() => {
    const unsub = async () => {
      const { data: partnerData } = await axiosInstance.get("/referral-agents");
      setPartners(partnerData);
    };
    unsub();
  }, []);

  const cols: ColumnDef<T>[] = tableCol({
    showActions,
    columns: [
      {
        accessorKey: "partner",
        header: "Order From",
        cell: ({ row }) => (
          <CommissionPartnerCell
            partners={partners}
            partnerId={(row.original as PartnerData).commission_partner_id}
          />
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created at",
        cell: ({ row }) => (
          <div className="capitalize">
            <p>
              {row.getValue("created_at")
                ? format(row.getValue("created_at"), "dd MMM y, HH:mm:ss")
                : "-"}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => (
          <div className="w-32">
            <Badge
              variant="outline"
              className="px-1.5 text-muted-foreground capitalize"
            >
              {(row.original as any).order_type}
            </Badge>
          </div>
        ),
      },
      {
        accessorKey: "pickup",
        header: "Pickup",
        cell: ({ row }) => (
          <div className="capitalize">
            <p>{(row.original as any).pickup}</p>
          </div>
        ),
      },
      {
        accessorKey: "dropoff",
        header: "Dropoff",
        cell: ({ row }) => (
          <div className="capitalize">
            <p>{(row.original as any).dropoff}</p>
          </div>
        ),
      },
      {
        accessorKey: "pickup_time",
        header: "Pickup Time",
        cell: ({ row }) => (
          <div className="capitalize">
            <p>
              {row.getValue("pickup_time")
                ? format(row.getValue("pickup_time"), "dd MMM y, HH:mm:ss")
                : "-"}
            </p>
          </div>
        ),
      },
      ...columns,
    ],
    EditComponent,
    responseMessage,
    resource,
  });

  return cols;
}
